import { useEffect, useRef, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import api, { formatApiErrorDetail } from "@/lib/api";
import { mediaUrl } from "@/lib/media";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import NoteMediaGallery from "@/components/NoteMediaGallery";
import { toast } from "sonner";
import { NotebookPen, Loader2, Trash2, Image as ImageIcon, Video, Paperclip, X } from "lucide-react";

export default function Notes() {
  const [students, setStudents] = useState([]);
  const [studentId, setStudentId] = useState("");
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [date, setDate] = useState(new Date().toISOString().slice(0, 10));
  const [content, setContent] = useState("");
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const [del, setDel] = useState(null);
  const inputRef = useRef(null);

  useEffect(() => {
    api.get("/students").then((r) => { setStudents(r.data); if (r.data.length) setStudentId(r.data[0].id); })
      .catch((e) => toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message));
  }, []);

  const load = async (sid) => {
    if (!sid) return;
    setLoading(true);
    try { const { data } = await api.get(`/notes?student_id=${sid}`); setNotes(data); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(studentId); }, [studentId]);

  const pick = (accept) => { if (inputRef.current) { inputRef.current.accept = accept; inputRef.current.click(); } };
  const addFiles = (e) => setFiles((p) => [...p, ...Array.from(e.target.files || [])]);
  const removeFile = (i) => setFiles((p) => p.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!content.trim() && files.length === 0) {
      toast.error("Not metni veya en az bir görsel/video ekleyin");
      return;
    }
    setSaving(true);
    try {
      const media_urls = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
        media_urls.push(data.url);
      }
      await api.post("/notes", { student_id: studentId, date, content: content.trim(), media_urls });
      toast.success("Not eklendi");
      setContent("");
      setFiles([]);
      load(studentId);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setSaving(false); }
  };

  const confirmDel = async () => {
    try { await api.delete(`/notes/${del.id}`); toast.success("Not silindi"); setDel(null); load(studentId); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
  };

  return (
    <DashboardLayout>
      <div className="p-4 sm:p-6 lg:p-10" data-testid="notes-page">
        <header className="mb-6">
          <p className="text-xs font-semibold uppercase tracking-[0.05em] text-slate-500">Takip</p>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-4xl">Günlük Notlar</h1>
          <p className="mt-2 text-sm text-slate-600">Öğrenci bazlı, tarihli günlük gelişim notları ve medya paylaşımı.</p>
        </header>

        <div className="mb-6 max-w-sm">
          <Select value={studentId} onValueChange={setStudentId}>
            <SelectTrigger className="h-11 rounded-xl" data-testid="notes-student-select"><SelectValue placeholder="Öğrenci seç" /></SelectTrigger>
            <SelectContent>{students.map((s) => <SelectItem key={s.id} value={s.id}>{s.full_name}</SelectItem>)}</SelectContent>
          </Select>
        </div>

        <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
          <Card className="rounded-2xl border-slate-200/60 p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)] lg:col-span-1" data-testid="note-form">
            <h3 className="mb-4 font-heading text-base font-semibold text-slate-800">Yeni Not</h3>
            <form onSubmit={submit} className="space-y-4">
              <div className="space-y-2"><Label>Tarih</Label><Input type="date" value={date} onChange={(e) => setDate(e.target.value)} data-testid="note-date-input" /></div>
              <div className="space-y-2"><Label>Not</Label><Textarea rows={5} value={content} onChange={(e) => setContent(e.target.value)} placeholder="Bugünkü gözlem..." data-testid="note-content-input" /></div>

              <div className="space-y-2">
                <Label>Görsel / Video</Label>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" variant="outline" size="sm" onClick={() => pick("image/*")} className="rounded-lg">
                    <ImageIcon className="mr-1 h-4 w-4" /> Fotoğraf
                  </Button>
                  <Button type="button" variant="outline" size="sm" onClick={() => pick("video/*")} className="rounded-lg">
                    <Video className="mr-1 h-4 w-4" /> Video
                  </Button>
                </div>
                <input ref={inputRef} type="file" multiple className="hidden" onChange={addFiles} />
                {files.length > 0 && (
                  <div className="space-y-2 rounded-xl border border-slate-200 bg-slate-50 p-3">
                    {files.map((f, i) => (
                      <div key={`${f.name}-${i}`} className="flex items-center justify-between gap-2 text-sm text-slate-700">
                        <span className="flex min-w-0 items-center gap-2 truncate">
                          <Paperclip className="h-3.5 w-3.5 flex-shrink-0 text-slate-400" />
                          <span className="truncate">{f.name}</span>
                        </span>
                        <button type="button" onClick={() => removeFile(i)} className="rounded-md p-1 text-slate-400 hover:bg-white hover:text-red-600">
                          <X className="h-4 w-4" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <Button type="submit" disabled={saving || !studentId} data-testid="note-save-button" className="w-full rounded-xl bg-violet-600 text-white hover:bg-violet-700">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Not Ekle"}
              </Button>
            </form>
          </Card>

          <div className="lg:col-span-2">
            {loading ? (
              <div className="flex justify-center py-20"><Loader2 className="h-8 w-8 animate-spin text-violet-600" /></div>
            ) : notes.length === 0 ? (
              <Card className="rounded-2xl border-dashed p-16 text-center text-slate-500" data-testid="notes-empty"><NotebookPen className="mx-auto mb-3 h-10 w-10 text-slate-300" />Bu öğrenci için not yok.</Card>
            ) : (
              <div className="space-y-3">
                {notes.map((n) => (
                  <Card key={n.id} data-testid={`note-card-${n.id}`} className="rounded-2xl border-slate-200/60 p-5 shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-xs font-semibold uppercase tracking-wide text-violet-600">{n.date}</p>
                        {n.content && <p className="mt-1 whitespace-pre-wrap text-sm text-slate-700">{n.content}</p>}
                        <NoteMediaGallery
                          mediaUrls={n.media_urls}
                          resolveUrl={mediaUrl}
                          testIdPrefix={`note-${n.id}`}
                        />
                      </div>
                      <Button variant="ghost" size="icon" onClick={() => setDel(n)} data-testid={`delete-note-${n.id}`} className="h-8 w-8 flex-shrink-0 rounded-lg text-red-600 hover:bg-red-50"><Trash2 className="h-3.5 w-3.5" /></Button>
                    </div>
                  </Card>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      <AlertDialog open={!!del} onOpenChange={(o) => !o && setDel(null)}>
        <AlertDialogContent className="rounded-2xl" data-testid="delete-note-dialog">
          <AlertDialogHeader><AlertDialogTitle>Notu sil?</AlertDialogTitle><AlertDialogDescription>Bu günlük not kalıcı olarak silinecek.</AlertDialogDescription></AlertDialogHeader>
          <AlertDialogFooter><AlertDialogCancel className="rounded-xl">Vazgeç</AlertDialogCancel><AlertDialogAction onClick={confirmDel} data-testid="confirm-delete-note" className="rounded-xl bg-red-600 hover:bg-red-700">Sil</AlertDialogAction></AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DashboardLayout>
  );
}
